/**
 * Coluna de contexto da ficha (como no Legado): a leitura da IA sobre o
 * cliente, os indicadores, as datas-chave e os pedidos do cliente para pular
 * direto a um deles. Cor só quando há o que fazer (a equipe deve, atraso).
 */
import React from "react";

import { formatarDataLocal, plural } from "../../../utils/dinheiro";
import IconeDaIA from "../IconeDaIA";
import { ESPERA_DA_PREVIA, contarEstados, quandoFoi } from "../apresentacao";
import {
  DateList,
  DateRow,
  EstadoComPonto,
  KpiCard,
  KpiGrid,
  KpiValue,
  LeituraDaIA,
  PedidoNavItem,
  PedidoNavList,
  RailCard,
  RailTitle,
  RotuloDoIndicador,
  SoNoComputador,
} from "./estilos";

/** A entrega prevista mais próxima de hoje em diante (AAAA-MM-DD compara como texto). */
function proximaEntrega(pedidos, agora = new Date()) {
  const dois = (n) => String(n).padStart(2, "0");
  const hoje = `${agora.getFullYear()}-${dois(agora.getMonth() + 1)}-${dois(agora.getDate())}`;
  return pedidos.map((p) => p.entrega_prevista).filter((d) => d && d >= hoje).sort()[0] || null;
}

function Indicadores({ cliente, pedidos, pendenciasAbertas }) {
  const ambientes = pedidos.flatMap((p) => p.ambientes || []);
  const esperandoEquipe = contarEstados(ambientes).find((c) => c.estado === "esperando_equipe")?.total || 0;
  return (
    <RailCard>
      <RailTitle>Indicadores</RailTitle>
      <KpiGrid>
        <KpiCard $tone="default">
          <RotuloDoIndicador>Ambientes</RotuloDoIndicador>
          <KpiValue>{ambientes.length}</KpiValue>
        </KpiCard>
        <KpiCard $tone={esperandoEquipe > 0 ? "warning" : "default"}>
          <RotuloDoIndicador>Esperando a equipe</RotuloDoIndicador>
          <KpiValue>{esperandoEquipe}</KpiValue>
        </KpiCard>
        <KpiCard $tone="default">
          <RotuloDoIndicador>Pendências abertas</RotuloDoIndicador>
          <KpiValue>{pendenciasAbertas}</KpiValue>
        </KpiCard>
        <KpiCard $tone={cliente.atraso_dias > 0 ? "danger" : "default"}>
          <RotuloDoIndicador>Atraso (dias)</RotuloDoIndicador>
          <KpiValue>{cliente.atraso_dias > 0 ? cliente.atraso_dias : 0}</KpiValue>
        </KpiCard>
      </KpiGrid>
    </RailCard>
  );
}

export default function LateralDaFicha({ cliente, pedidos, pendenciasAbertas, pedidoAtivo, aoIrAoPedido }) {
  const espera = ESPERA_DA_PREVIA[cliente.quem_espera] || null;
  // Datas do cliente (todos os pedidos), não só do pedido desta ficha.
  const proxima = proximaEntrega(pedidos);
  const projetistas = [...new Set(pedidos.flatMap((p) => p.projetistas || []))];
  return (
    <>
      <LeituraDaIA aria-label="Leitura da IA sobre o cliente">
        <header>
          <IconeDaIA tamanho={14} />
          Leitura da IA
        </header>
        {espera ? <EstadoComPonto $tom={espera.tom}>{espera.rotulo}</EstadoComPonto> : null}
        <p>{cliente.resumo || "A IA ainda não analisou as conversas deste cliente."}</p>
      </LeituraDaIA>

      <Indicadores cliente={cliente} pedidos={pedidos} pendenciasAbertas={pendenciasAbertas} />

      <SoNoComputador>
        <RailCard>
          <RailTitle>Datas-chave</RailTitle>
          <DateList>
            <DateRow>
              <span>Próxima entrega</span>
              <span>{proxima ? formatarDataLocal(proxima) : "Sem data"}</span>
            </DateRow>
            <DateRow>
              <span>Projetistas</span>
              <span>{projetistas.join(", ") || "Sem projetista"}</span>
            </DateRow>
            <DateRow>
              <span>Última mensagem</span>
              <span>{cliente.ultima_mensagem_em ? quandoFoi(cliente.ultima_mensagem_em) : "Sem mensagem"}</span>
            </DateRow>
          </DateList>
        </RailCard>
      </SoNoComputador>

      {pedidos.length > 1 ? (
        <SoNoComputador>
          <RailCard>
            <RailTitle>Pedidos do cliente ({pedidos.length})</RailTitle>
            <PedidoNavList>
              {pedidos.map((pedido) => (
                <PedidoNavItem
                  key={pedido.numero_pedido}
                  type="button"
                  $active={String(pedidoAtivo) === String(pedido.numero_pedido)}
                  aria-label={`Ir ao pedido ${pedido.numero_pedido}`}
                  onClick={() => aoIrAoPedido(pedido.numero_pedido)}
                >
                  <strong>Pedido {pedido.numero_pedido}{pedido.atual ? " (esta ficha)" : ""}</strong>
                  <span>
                    {plural((pedido.ambientes || []).length, "ambiente", "ambientes")}
                    {pedido.etapa_rotulo ? ` · ${pedido.etapa_rotulo}` : ""}
                    {pedido.atraso_dias > 0 ? ` · ${pedido.atraso_dias} d de atraso` : ""}
                  </span>
                </PedidoNavItem>
              ))}
            </PedidoNavList>
          </RailCard>
        </SoNoComputador>
      ) : null}
    </>
  );
}
